/**
 * Multi-Agent Orchestration Test Page
 * 
 * Live demonstration of count-based multi-agent provisioning with:
 * - Real-time progress tracking through all 4 phases
 * - Visual agent status display
 * - Results aggregation and insights
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Users, 
  Activity,
  Brain,
  Network,
  Zap
} from 'lucide-react';
import { multiAgentOrchestrator, OrchestrationResult, AgentInstance } from '@/services/multiAgentOrchestrator';
import { agentCacheManager } from '@/services/agentCacheManager';
import { circuitBreakerManager } from '@/services/agentCircuitBreaker';
import { retryStrategyManager } from '@/services/agentRetryStrategy';

export default function MultiAgentTest() {
  const [userRequest, setUserRequest] = useState(
    'Find 3 emails by contact (alice@company.com, bob@company.com, charlie@company.com) and search 2 topics (project_alpha, budget_review)'
  );
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<string>('');
  const [agentStatuses, setAgentStatuses] = useState<Map<string, AgentInstance>>(new Map());
  const [result, setResult] = useState<OrchestrationResult | null>(null);
  const [error, setError] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);
  const [cacheStats, setCacheStats] = useState<any>(null);
  const [circuitHealth, setCircuitHealth] = useState<any>(null);
  const [retryBudget, setRetryBudget] = useState<any>(null);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const runOrchestration = async () => {
    setIsRunning(true);
    setError('');
    setResult(null);
    setAgentStatuses(new Map());
    setLogs([]);
    
    addLog('🚀 Starting multi-agent orchestration...');
    
    try {
      const orchestrationResult = await multiAgentOrchestrator.orchestrate(
        userRequest,
        (update) => {
          setCurrentPhase(update.phase);
          
          if (update.phase === 'parsing') {
            addLog('🔍 Phase 1: Parsing intent with count recognition');
          } else if (update.phase === 'provisioning') {
            addLog(`🤖 Phase 2: Provisioning ${update.data.plan?.total_agents_needed || '?'} agents`);
          } else if (update.phase === 'executing') {
            if (update.data.agent_id) {
              const agent = update.data as AgentInstance;
              setAgentStatuses(prev => new Map(prev).set(agent.agent_id, agent));
              
              if (agent.status === 'running') {
                addLog(`   ⚡ ${agent.agent_id}: Processing ${agent.target}`);
              } else if (agent.status === 'completed') {
                addLog(`   ✅ ${agent.agent_id}: Completed in ${agent.duration_ms}ms`);
              } else if (agent.status === 'failed') {
                addLog(`   ❌ ${agent.agent_id}: Failed - ${agent.error}`);
              }
            } else {
              addLog('⚡ Phase 4: Executing agents in parallel waves');
            }
          } else if (update.phase === 'aggregating') {
            addLog('📊 Aggregating results and generating insights');
          }
        }
      );
      
      setResult(orchestrationResult);
      addLog(`✅ Orchestration complete! Success rate: ${(orchestrationResult.workflow_summary.success_rate * 100).toFixed(1)}%`);
      
      // Update system metrics
      setCacheStats(agentCacheManager.getStats());
      setCircuitHealth(circuitBreakerManager.getHealthSummary());
      setRetryBudget(retryStrategyManager.getAllBudgetStatus());
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      addLog(`❌ Orchestration failed: ${errorMsg}`);
    } finally {
      setIsRunning(false);
      setCurrentPhase('');
    }
  };

  const exampleRequests = [
    'Find 3 emails by contact and search 2 topics',
    'Find meetings by 2 users and by 3 topics, then conduct web research',
    'Search emails from 4 contacts about project updates',
    'Find calendar events with 3 attendees and search 2 topics in emails'
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Network className="h-8 w-8 text-primary" />
          Multi-Agent Orchestration Test
        </h1>
        <p className="text-muted-foreground">
          Test count-based multi-agent provisioning with Microsoft Graph API operations
        </p>
      </div>

      {/* Phase Indicator */}
      {isRunning && (
        <Card className="p-4 bg-primary/5 border-primary">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <div>
              <div className="font-semibold">Current Phase: {currentPhase}</div>
              <div className="text-sm text-muted-foreground">
                {currentPhase === 'parsing' && 'Analyzing user intent and identifying count-based tasks...'}
                {currentPhase === 'provisioning' && 'Creating specialized agents for each task...'}
                {currentPhase === 'executing' && 'Running agents in parallel waves...'}
                {currentPhase === 'aggregating' && 'Synthesizing results and generating insights...'}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Input Section */}
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              User Request (with count-based requirements)
            </label>
            <Textarea
              value={userRequest}
              onChange={(e) => setUserRequest(e.target.value)}
              placeholder="e.g., Find 3 emails by contact, 2 meetings by user, and search 3 topics"
              rows={3}
              className="font-mono text-sm"
              disabled={isRunning}
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Example Requests:</div>
            <div className="flex flex-wrap gap-2">
              {exampleRequests.map((example, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => setUserRequest(example)}
                  disabled={isRunning}
                >
                  {example}
                </Button>
              ))}
            </div>
          </div>

          <Button 
            onClick={runOrchestration} 
            disabled={isRunning || !userRequest.trim()}
            className="w-full"
            size="lg"
          >
            {isRunning ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Running Orchestration...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-5 w-5" />
                Run Multi-Agent Orchestration
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Agent Status Grid */}
      {agentStatuses.size > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Agent Status ({agentStatuses.size} agents)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from(agentStatuses.values()).map((agent) => (
              <div 
                key={agent.agent_id}
                className="p-3 border rounded-lg bg-card"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-mono text-sm font-semibold truncate">
                    {agent.agent_id}
                  </div>
                  <Badge 
                    variant={
                      agent.status === 'completed' ? 'default' : 
                      agent.status === 'failed' ? 'destructive' : 
                      'secondary'
                    }
                  >
                    {agent.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                    {agent.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                    {agent.status === 'running' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    {agent.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                    {agent.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="truncate"><span className="font-medium">Target:</span> {agent.target}</div>
                  <div><span className="font-medium">Task:</span> {agent.task_id}</div>
                  {agent.duration_ms && (
                    <div><span className="font-medium">Duration:</span> {agent.duration_ms}ms</div>
                  )}
                  {agent.error && (
                    <div className="text-destructive text-xs mt-1">{agent.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Results Tabs */}
      {result && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Orchestration Results
          </h2>

          <Tabs defaultValue="summary" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="summary">Summary</TabsTrigger>
              <TabsTrigger value="results">Results</TabsTrigger>
              <TabsTrigger value="insights">Insights</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
              <TabsTrigger value="raw">Raw Data</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground mb-1">Total Tasks</div>
                  <div className="text-2xl font-bold">{result.workflow_summary.total_tasks}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground mb-1">Agents Deployed</div>
                  <div className="text-2xl font-bold">{result.workflow_summary.total_agents_deployed}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground mb-1">Success Rate</div>
                  <div className="text-2xl font-bold text-green-600">
                    {(result.workflow_summary.success_rate * 100).toFixed(1)}%
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground mb-1">Duration</div>
                  <div className="text-2xl font-bold">{result.workflow_summary.execution_time_ms}ms</div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="results">
              <div className="space-y-4">
                {Object.entries(result.results_by_category).map(([category, data]) => (
                  <Card key={category} className="p-4">
                    <h3 className="font-semibold text-lg mb-2 capitalize">{category} Results</h3>
                    <div className="text-sm text-muted-foreground mb-3">
                      {data.agents_deployed} agents deployed | {data.results?.length || 0} results
                    </div>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs overflow-auto max-h-60">
                      {JSON.stringify(data.results, null, 2)}
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="insights">
              <div className="space-y-3">
                {result.cross_correlation_insights.map((insight, idx) => (
                  <Card key={idx} className="p-4">
                    <div className="flex items-start gap-3">
                      <Brain className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="text-sm">{insight}</div>
                    </div>
                  </Card>
                ))}
                {result.cross_correlation_insights.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    No cross-correlation insights generated
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="actions">
              <div className="space-y-3">
                {result.recommended_actions.map((action, idx) => (
                  <Card key={idx} className="p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">{action}</div>
                    </div>
                  </Card>
                ))}
                {result.recommended_actions.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    No recommended actions generated
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="raw">
              <div className="bg-muted p-4 rounded-lg font-mono text-xs overflow-auto max-h-96">
                {JSON.stringify(result, null, 2)}
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      )}

      {/* System Metrics */}
      {(cacheStats || circuitHealth || retryBudget) && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Metrics (Production Features)
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Cache Statistics */}
            {cacheStats && (
              <Card className="p-4 bg-blue-50 dark:bg-blue-950">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-600" />
                  Cache Performance
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hit Rate:</span>
                    <span className="font-semibold text-green-600">
                      {(cacheStats.hitRate * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Entries:</span>
                    <span className="font-semibold">{cacheStats.totalEntries}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cache Size:</span>
                    <span className="font-semibold">
                      {(cacheStats.totalSize / 1024).toFixed(2)} KB
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Evictions:</span>
                    <span className="font-semibold">{cacheStats.evictions}</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Circuit Breaker Health */}
            {circuitHealth && (
              <Card className="p-4 bg-green-50 dark:bg-green-950">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Circuit Breaker Health
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Healthy:</span>
                    <span className="font-semibold text-green-600">
                      {circuitHealth.healthy}/{circuitHealth.total}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Degraded:</span>
                    <span className="font-semibold text-yellow-600">
                      {circuitHealth.degraded}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Failing:</span>
                    <span className="font-semibold text-red-600">
                      {circuitHealth.failing}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Agents:</span>
                    <span className="font-semibold">{circuitHealth.total}</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Retry Budget */}
            {retryBudget && Object.keys(retryBudget).length > 0 && (
              <Card className="p-4 bg-orange-50 dark:bg-orange-950">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  Retry Budget Status
                </h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(retryBudget).slice(0, 3).map(([agentId, stats]: [string, any]) => (
                    <div key={agentId} className="space-y-1">
                      <div className="font-mono text-xs text-muted-foreground truncate">
                        {agentId}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs">Used:</span>
                        <span className="font-semibold text-xs">
                          {stats.used}/{stats.total}
                        </span>
                      </div>
                    </div>
                  ))}
                  {Object.keys(retryBudget).length > 3 && (
                    <div className="text-xs text-muted-foreground text-center pt-2">
                      +{Object.keys(retryBudget).length - 3} more agents
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </Card>
      )}

      {/* Execution Logs */}
      {logs.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Execution Logs
          </h2>
          <div className="bg-black text-green-400 p-4 rounded-lg font-mono text-xs space-y-1 max-h-96 overflow-auto">
            {logs.map((log, idx) => (
              <div key={idx}>{log}</div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
